import React, { useEffect, useRef, useState } from 'react';
import { InputProp, OptionType } from '../../utils/tsConfig';
import { RiArrowDropDownLine, RiArrowDropUpLine } from 'react-icons/ri';

const Drop: React.FC<InputProp> = (props: any) => {
	const { type, setState, state, isArray, arrayName, indexVal, returnKey, fieldName, className = '', options = [], textCase = null, optionDisplay, optionKey, required = false, disabled = false, onChange, optionsAll, typable, didntShowKey, valueShowKey, dropdownWidthStyle, keyId } = props;
	const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(options || []);
	const [selected, setSelected] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [inputDropValue, setInputDropValue] = useState('');
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [searchText, setSearchText] = useState(''); // Move this up
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const mainInputRef = useRef<HTMLInputElement | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);

	// Keep filteredOptions in sync with options if no search is applied
	useEffect(() => {
		if (Array.isArray(options)) {
			setFilteredOptions(options);
		}
	}, [options]);

	// If we have array data, initialize the selected value from array field
	useEffect(() => {
		if (isArray && arrayName && indexVal !== undefined && returnKey && state) {
			try {
				const arrValue = state?.[arrayName]?.[indexVal]?.[returnKey];
				if (typeof arrValue !== 'undefined') {
					setSelected(String(arrValue));
				}
			} catch (error) {
				console.error('Error accessing array value:', error);
			}
		}
	}, [state, isArray, arrayName, indexVal, returnKey]);

	// If we have a returnKey, optionKey & optionDisplay, set selected from (optionsAll || options)
	useEffect(() => {
		if (returnKey && optionKey && optionDisplay && state?.[returnKey]) {
			try {
				const dataset = (optionsAll || options)?.find((option: any) => option?.[optionKey] === state?.[returnKey]);
				if (dataset && dataset[optionDisplay]) {
					setSelected(String(dataset[optionDisplay]));
				}
			} catch (error) {
				console.error('Error finding dataset:', error);
			}
		}
	}, [returnKey, optionKey, optionDisplay, state, optionsAll, options]);

	// Single outside click handler to hide dropdown
	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			const target = event.target as HTMLElement;
			if (dropdownRef.current && !dropdownRef.current.contains(target) && mainInputRef.current !== target) {
				setIsDropdownVisible(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Disable wheel scroll on input focus
	useEffect(() => {
		const input = inputRef.current;
		if (input) {
			const handleWheel = (e: WheelEvent) => {
				if (document.activeElement === input) {
					e.preventDefault();
				}
			};
			input.addEventListener('wheel', handleWheel);
			return () => {
				input.removeEventListener('wheel', handleWheel);
			};
		}
	}, []);

	// Keep inputValue in sync based on selected, state, and various props
	useEffect(() => {
		try {
			let newValue: any = '';

			// For normal usage
			if (!didntShowKey) {
				if (isArray) {
					newValue = selected;
				} else if (returnKey && state) {
					newValue = valueShowKey ? state?.[returnKey]?.[valueShowKey] : state?.[returnKey];
				} else {
					newValue = state;
				}
			} else {
				// If we have 'didntShowKey'
				if (selected) {
					newValue = selected;
				} else if (returnKey && state) {
					newValue = valueShowKey ? state?.[returnKey]?.[valueShowKey] : state?.[returnKey];
				} else if (optionDisplay && !returnKey && state) {
					newValue = state?.[optionDisplay];
				} else {
					newValue = state;
				}
			}

			setInputValue(newValue || '');

			// If typable, always show state
			if (typable) {
				newValue = state;
			}
			setInputDropValue(newValue || '');
		} catch (error) {
			console.error('Error updating input value:', error);
			setInputValue('');
			setInputDropValue('');
		}
	}, [state, isArray, selected, returnKey, valueShowKey, didntShowKey, typable, optionDisplay]);

	// If array & optionKey/optionDisplay, sync displayed text to matching item
	useEffect(() => {
		try {
			if (isArray && optionKey && optionDisplay && Array.isArray(options) && options.length) {
				const setSelectedValue = options.find((x) => x && x[optionKey] === inputValue)?.[optionDisplay];
				if (setSelectedValue && setSelectedValue !== inputValue) {
					setInputValue(String(setSelectedValue));
				}
			} else {
				// Clear if no state or no array
				if (returnKey) {
					if (!state || !state?.[returnKey]) {
						setInputValue('');
						setInputDropValue('');
						setSelected('');
					}
				} else if (!state) {
					setInputValue('');
					setInputDropValue('');
					setSelected('');
				}
			}
		} catch (error) {
			console.error('Error in array sync effect:', error);
		}
	}, [state, returnKey, isArray, inputValue, optionKey, optionDisplay, options]);

	const handleMainInputFocus = () => {
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
		setIsDropdownVisible(true);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const { value } = e.target;

			// If array scenario
			if (isArray && arrayName && indexVal !== undefined && returnKey && setState && state) {
				const updatedState = { ...state };
				if (!updatedState[arrayName]) {
					updatedState[arrayName] = [];
				}
				while (updatedState[arrayName].length <= indexVal) {
					updatedState[arrayName].push({});
				}
				if (typeof updatedState[arrayName][indexVal] !== 'object' || updatedState[arrayName][indexVal] === null) {
					updatedState[arrayName][indexVal] = {};
				}
				updatedState[arrayName][indexVal][returnKey] = value;
				setState(updatedState);
				setSelected(value);
				setIsDropdownVisible(true);
			} else {
				// Normal scenario
				setSelected(value);
				if (setState) {
					if (returnKey) {
						setState({ ...state, [returnKey]: value });
					} else {
						setState(value);
					}
				}
				if (onChange) {
					onChange({ value });
				}
				setIsDropdownVisible(true);
			}
		} catch (error) {
			console.error('Error handling input change:', error);
		}
	};

	const handleSelect = (optionVal: any) => {
		try {
			const displayValue = optionDisplay && optionVal ? optionVal[optionDisplay] : optionVal;
			setSearchText('');

			if (isArray && arrayName && indexVal !== undefined && returnKey && setState && state) {
				const updatedState = { ...state };
				if (!updatedState[arrayName]) {
					updatedState[arrayName] = [];
				}
				while (updatedState[arrayName].length <= indexVal) {
					updatedState[arrayName].push({});
				}
				updatedState[arrayName][indexVal][returnKey] = optionKey && optionVal ? optionVal[optionKey] : optionVal;
				setState(updatedState);
				setSelected(displayValue ? String(displayValue) : String(optionVal));
				setIsDropdownVisible(false);
			} else {
				if (setState) {
					if (returnKey) {
						setState({
							...state,
							[returnKey]: optionKey && optionVal ? optionVal[optionKey] : optionVal,
						});
					} else {
						setState(optionKey && optionVal ? optionVal[optionKey] : optionVal);
					}
				}
				if (onChange) {
					if (optionDisplay) {
						onChange({
							value: optionKey && optionVal ? optionVal[optionKey] : optionVal,
							name: displayValue,
						});
					} else {
						onChange({ value: optionKey && optionVal ? optionVal[optionKey] : optionVal });
					}
				}
				setSelected(displayValue ? String(displayValue) : String(optionVal));
			}
			setIsDropdownVisible(false);
		} catch (error) {
			console.error('Error handling select:', error);
		}
	};

	const handleClearSelectedOption = () => {
		try {
			setInputDropValue('');
			setSelected('');
			setInputValue('');
			setFilteredOptions(options || []);

			if (setState) {
				if (isArray && arrayName && indexVal !== undefined && returnKey && state) {
					const updatedState = { ...state };
					if (updatedState[arrayName]?.[indexVal]) {
						updatedState[arrayName][indexVal][returnKey] = '';
					}
					setState(updatedState);
				} else {
					if (returnKey) {
						setState({ ...state, [returnKey]: '' });
					} else {
						setState('');
					}
				}
			}
		} catch (error) {
			console.error('Error clearing selection:', error);
		}
	};

	const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const value = e.target.value;
			setInputDropValue(value);

			if (Array.isArray(options)) {
				const filtered = options.filter((x) => {
					if (!x) return false;
					const searchValue = optionDisplay ? x[optionDisplay] : x;
					return searchValue
						?.toString()
						?.toLowerCase()
						.includes(value?.toLowerCase() || '');
				});
				setFilteredOptions(filtered);
			}

			// If not typable and search is empty, clear selection
			if (!typable && value === '') {
				handleClearSelectedOption();
			}
		} catch (error) {
			console.error('Error handling search input change:', error);
		}
	};

	if (!fieldName) {
		return null;
	}

	return (
		<div key={keyId} style={{ position: 'relative' }}>
			<div ref={dropdownRef} className='relative w-full'>
				<div className={`customInput ${className}`} key={keyId}>
					<input id={'dropDown' + (returnKey || 'default')} ref={mainInputRef} disabled={disabled} required={required} value={inputValue || ''} className='input__field dark:bg-[#202020f5] dark:text-white' type={type || 'text'} placeholder=' ' readOnly={!typable} onFocus={handleMainInputFocus} onChange={handleInputChange} />
					<label htmlFor={'dropDown' + (returnKey || 'default')} className='input__label dark:bg-[#202020f5] dark:border-0 dark:text-[#ffffffcb] cursor-pointer'>
						{fieldName.replace(/\*$/, '')}
						{fieldName.endsWith('*') && <span className='mandatory-asterisk'>*</span>}
					</label>

					<button type='button' onClick={() => setIsDropdownVisible((prev) => !prev)} className='absolute right-2 top-1/2 -translate-y-1/2 dark:text-white text-3xl cursor-pointer z-[150]'>
						{isDropdownVisible ? <RiArrowDropUpLine /> : <RiArrowDropDownLine />}
					</button>
				</div>

				{isDropdownVisible && (
					<div key={keyId} className='absolute z-[140] w-full mt-2'>
						{!typable && <input id={`searchInput${returnKey || 'default'}`} ref={searchInputRef} placeholder='Search...' type='search' value={inputDropValue || ''} className='w-full max-w-md px-2 py-2 text-md border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 transition duration-300 h-[30px] ms-1 bg-slate-100 text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600' onChange={handleSearchInputChange} />}

						<ul id='scrollBarEnable' className='ms-1 w-full mt-1 bg-slate-100 border border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto' style={dropdownWidthStyle}>
							{Array.isArray(filteredOptions) &&
								filteredOptions.map((option, index) => {
									if (!option) return null;

									let optionValue = optionDisplay ? option[optionDisplay] : option;
									if (optionValue) {
										if (textCase === 'upper') {
											optionValue = optionValue.toString().toUpperCase();
										} else if (textCase === 'lower') {
											optionValue = optionValue.toString().toLowerCase();
										} else if (textCase === 'capitalize') {
											optionValue = optionValue.toString().charAt(0).toUpperCase() + optionValue.toString().slice(1);
										}
									}

									return (
										<li key={index} onClick={() => handleSelect(option)} className='p-2 cursor-pointer text-gray-900 hover:bg-gray-100 dark:text-white dark:bg-gray-800 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600'>
											{optionValue || ''}
										</li>
									);
								})}
						</ul>
					</div>
				)}
			</div>
		</div>
	);
};

export default Drop;
